// ════════════════════════════════════════════════════════════════════
// Kanbify Express-shim client. It preserves the old query-builder surface
// used by the app, but there is no external backend fallback: local and live
// both talk to the same-origin shim.
//
// Builder is THENABLE (await kanbify.from('x').select().eq()...).
// realtime .channel() = no-op stub; real freshness via polling (WS4,
// Pontify pattern: interval + tab-focus).
// ════════════════════════════════════════════════════════════════════

const SESSION_KEY = 'kanbify.shim.session'

function loadSession() {
  try {
    const s = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null')
    if (s && s.expires_at && s.expires_at * 1000 > Date.now()) return s
  } catch { /* ignore */ }
  return null
}
function saveSession(s) {
  if (s) localStorage.setItem(SESSION_KEY, JSON.stringify(s))
  else localStorage.removeItem(SESSION_KEY)
}

export function createShimClient(baseUrl) {
  const BASE = (baseUrl || '').replace(/\/$/, '')
  const authListeners = new Set()

  function authHeaders() {
    const s = loadSession()
    return s?.access_token ? { Authorization: `Bearer ${s.access_token}` } : {}
  }

  async function post(pathname, body) {
    const res = await fetch(`${BASE}${pathname}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body || {}),
    })
    let json = null
    try { json = await res.json() } catch { /* 204 */ }
    return { res, json }
  }

  // ---- PostgREST-ish builder ----
  function from(table) {
    const state = { select: '*', filters: [], order: null, limit: null, range: null,
                    single: false, maybeSingle: false, op: 'select' }
    const b = {}
    const f = (col, op, val) => { state.filters.push({ col, op, val }); return b }
    b.select = (s = '*') => { state.select = s; return b }
    b.insert = (rows) => { state.op = 'insert'; state.rows = rows; return b }
    b.upsert = (rows) => { state.op = 'upsert'; state.rows = rows; return b }
    b.update = (patch) => { state.op = 'update'; state.patch = patch; return b }
    b.delete = () => { state.op = 'delete'; return b }
    b.eq = (c, v) => f(c, 'eq', v)
    b.neq = (c, v) => f(c, 'neq', v)
    b.gt = (c, v) => f(c, 'gt', v)
    b.gte = (c, v) => f(c, 'gte', v)
    b.lt = (c, v) => f(c, 'lt', v)
    b.lte = (c, v) => f(c, 'lte', v)
    b.like = (c, v) => f(c, 'like', v)
    b.ilike = (c, v) => f(c, 'ilike', v)
    b.is = (c, v) => f(c, 'is', v)
    b.in = (c, v) => f(c, 'in', v)
    b.contains = (c, v) => f(c, 'contains', v)
    b.not = (c, _op, v) => f(c, 'neq', v)
    b.filter = (c, op, v) => f(c, op.replace(/^.*\./, ''), v)
    b.match = (obj) => { Object.entries(obj).forEach(([c, v]) => f(c, 'eq', v)); return b }
    b.or = (expr) => {
      // "col.eq.val,col2.eq.val2" → OR group. Split only on the first
      // two dots so ISO timestamps in the value (2026-05-16T00:00:00.000Z)
      // and dotted relation paths (lists.boards.company_id.eq.X) survive.
      const grp = expr.split(',').map((p) => {
        const i1 = p.indexOf('.')
        const i2 = p.indexOf('.', i1 + 1)
        const col = p.slice(0, i1)
        const op = p.slice(i1 + 1, i2)
        let val = p.slice(i2 + 1)
        if (val === 'null') val = null
        return { col, op, val }
      })
      state.filters.push({ op: 'or', val: grp }); return b
    }
    b.order = (c, opts = {}) => {
      state.order = state.order || []
      state.order.push({ col: c, asc: opts.ascending !== false }); return b
    }
    b.limit = (n) => { state.limit = n; return b }
    b.range = (a, z) => { state.range = [a, z]; return b }
    b.single = () => { state.single = true; return b }
    b.maybeSingle = () => { state.maybeSingle = true; return b }
    b.then = (resolve, reject) =>
      post(`/data/${table}`, state)
        .then(({ json }) => resolve(json || { data: null, error: null }))
        .catch(reject)
    return b
  }

  async function rpc(fn, args) {
    const { json } = await post(`/rpc/${fn}`, args || {})
    return json || { data: null, error: null }
  }

  // ---- auth (GoTrue-shaped) ----
  const auth = {
    async getSession() {
      return { data: { session: loadSession() }, error: null }
    },
    async getUser() {
      const s = loadSession()
      return { data: { user: s?.user || null }, error: s ? null : { message: 'no session' } }
    },
    async signInWithPassword({ email, password, remember }) {
      const { res, json } = await post('/auth/v1/token', { email, password, remember })
      if (!res.ok) return { data: { session: null, user: null }, error: { message: json?.error_description || 'login failed' } }
      saveSession(json)
      authListeners.forEach((cb) => cb('SIGNED_IN', json))
      return { data: { session: json, user: json.user }, error: null }
    },
    async signOut() {
      await post('/auth/v1/logout')
      saveSession(null)
      authListeners.forEach((cb) => cb('SIGNED_OUT', null))
      return { error: null }
    },
    // Upfront check of an invite/join link validity (shows expired/used/invalid
    // before the user fills the form). Returns { valid, code?, email?, kind? }.
    async checkInvite({ inviteToken, joinToken } = {}) {
      const qs = new URLSearchParams()
      if (inviteToken) qs.set('invite', inviteToken)
      if (joinToken) qs.set('join', joinToken)
      try {
        const res = await fetch(`${BASE}/auth/v1/invite-check?${qs.toString()}`, { headers: { ...authHeaders() } })
        return await res.json()
      } catch { return { valid: false, code: 'check_failed' } }
    },
    async signUp({ email, password, options } = {}) {
      // Invitation-only: the shim creates an account only with a valid
      // pending invite token. Token + full_name ride in options.data
      // (set by AuthContext from the ?invite= link).
      const token = options?.data?.invite_token
      const full_name = options?.data?.full_name
      const { res, json } = await post('/auth/v1/signup', { email, password, token, full_name })
      if (!res.ok) {
        return { data: { user: null, session: null }, error: {
          message: json?.error_description || json?.error || 'Înregistrarea e doar pe bază de invitație.',
          code: json?.code || null,
        } }
      }
      saveSession(json)
      authListeners.forEach((cb) => cb('SIGNED_IN', json))
      return { data: { session: json, user: json.user }, error: null }
    },
    // Per-company self-register link (?join=TOKEN). Creates the account
    // straight into the company and logs in. Same error/code surface as
    // signUp so AuthPage can pivot to Login on account_exists, etc.
    async signUpViaCompanyLink({ email, joinToken, fullName } = {}) {
      // No password here — it is chosen on the email-confirm screen.
      const { res, json } = await post('/auth/v1/join', {
        email, join_token: joinToken, full_name: fullName,
      })
      if (!res.ok) {
        return { data: { user: null, session: null }, error: {
          message: json?.error_description || json?.error || 'Link de invitație invalid.',
          code: json?.code || null,
        } }
      }
      // Email-confirmed flow: server returns { pending:true } — NO
      // session yet. Account is created only after the emailed link.
      return { data: { pending: !!json?.pending, email: json?.email || email, session: null }, error: null }
    },
    // Confirm a ?confirm_signup= link → server creates the real account
    // and returns a session (logs the user in).
    async confirmSignup(token, password) {
      const { res, json } = await post('/auth/v1/confirm-signup', { token, password })
      if (!res.ok) {
        return { data: { session: null }, error: {
          message: json?.error_description || json?.error || 'Link de confirmare invalid sau expirat.',
          code: json?.code || null,
        } }
      }
      saveSession(json)
      authListeners.forEach((cb) => cb('SIGNED_IN', json))
      return { data: { session: json, user: json.user }, error: null }
    },
    async resetPasswordForEmail(email, opts = {}) {
      const { res, json } = await post('/auth/v1/recover', {
        email, redirect_to: opts.redirectTo,
      })
      // Always "succeeds" (no user enumeration); only network errors fail.
      return res.ok ? { data: {}, error: null }
        : { data: null, error: { message: (json && json.error_description) || 'reset failed' } }
    },
    // Confirm a reset link (token from ?reset_token=) with a new password.
    async confirmRecovery(token, password) {
      const { res, json } = await post('/auth/v1/recover/confirm', { token, password })
      return res.ok ? { data: {}, error: null }
        : { data: null, error: { message: (json && json.error_description) || 'reset failed' } }
    },
    onAuthStateChange(cb) {
      authListeners.add(cb)
      // emit current state async (matches kanbify-js behaviour)
      Promise.resolve().then(() => cb(loadSession() ? 'INITIAL_SESSION' : 'INITIAL_SESSION', loadSession()))
      return { data: { subscription: { unsubscribe: () => authListeners.delete(cb) } } }
    },
  }

  // ---- storage (disk-backed) ----
  const storage = {
    from(bucket) {
      return {
        async upload(key, file) {
          const res = await fetch(`${BASE}/storage/v1/object/${bucket}/${key}`, {
            method: 'POST', headers: authHeaders(), body: file,
          })
          return res.ok ? { data: { path: key }, error: null } : { data: null, error: { message: 'upload failed' } }
        },
        async remove(keys) {
          for (const k of keys) {
            await fetch(`${BASE}/storage/v1/object/${bucket}/${k}`, { method: 'DELETE', headers: authHeaders() })
          }
          return { data: {}, error: null }
        },
        getPublicUrl(key) {
          return { data: { publicUrl: `${BASE}/storage/v1/object/public/${bucket}/${key}` } }
        },
      }
    },
  }

  // ---- realtime → polling (Pontify pattern: interval + tab-focus) ----
  // Drop-in for kanbify.channel(): every existing
  // `.channel(n).on('postgres_changes',…,cb).subscribe()` call site
  // (24 of them) transparently becomes interval+visibility polling — the
  // `cb` is the hook's debounced refetch, so invoking it periodically
  // refreshes data with no per-hook changes. Presence degrades
  // gracefully (heartbeat table is the real presence source now).
  const POLL_MS = Number(import.meta.env.VITE_SHIM_POLL_MS) || 15000

  function channel(name) {
    const pgCbs = []
    const presenceCbs = []
    let timer = null
    let visHandler = null
    let mine = {}
    const ch = {}
    ch.on = (type, a, b) => {
      const cb = typeof b === 'function' ? b : a
      if (type === 'postgres_changes') pgCbs.push(cb)
      else if (type === 'presence') presenceCbs.push(cb)
      return ch // broadcast/other → ignored, still chainable
    }
    ch.presenceState = () => ({ [name || 'shim']: Object.values(mine) })
    ch.track = async (payload) => {
      mine = { self: payload }
      presenceCbs.forEach((c) => { try { c() } catch { /* noop */ } })
      return 'ok'
    }
    ch.untrack = async () => { mine = {}; return 'ok' }
    ch.send = async () => 'ok'
    ch.subscribe = (statusCb) => {
      if (typeof statusCb === 'function') { try { statusCb('SUBSCRIBED') } catch { /* noop */ } }
      presenceCbs.forEach((c) => { try { c() } catch { /* noop */ } })
      const fire = () => {
        if (typeof document !== 'undefined' && document.hidden) return
        pgCbs.forEach((c) => {
          try { c({ eventType: '*', schema: 'public', new: {}, old: {} }) } catch { /* noop */ }
        })
      }
      timer = setInterval(fire, POLL_MS + Math.floor(Math.random() * 3000))
      if (typeof document !== 'undefined') {
        visHandler = () => { if (!document.hidden) fire() }
        document.addEventListener('visibilitychange', visHandler)
      }
      return ch
    }
    ch.unsubscribe = () => {
      if (timer) { clearInterval(timer); timer = null }
      if (visHandler && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', visHandler)
        visHandler = null
      }
      return Promise.resolve('ok')
    }
    return ch
  }
  function removeChannel(ch) {
    if (ch && typeof ch.unsubscribe === 'function') return ch.unsubscribe()
    return Promise.resolve('ok')
  }

  // ---- functions.invoke (edge-fn compat → shim /functions/v1) ----
  const functions = {
    async invoke(name, opts = {}) {
      try {
        const res = await fetch(`${BASE}/functions/v1/${name}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
          body: JSON.stringify(opts.body || {}),
        })
        let data = null
        try { data = await res.json() } catch { /* no body */ }
        if (!res.ok) return { data: null, error: { message: (data && data.error) || `HTTP ${res.status}`, status: res.status } }
        return { data, error: null }
      } catch (e) {
        return { data: null, error: { message: e.message } }
      }
    },
  }

  return { from, rpc, auth, storage, channel, removeChannel, functions }
}
